/**
 * Fetch live menu and contact data for the preview homepage.
 * Duplicated on purpose so we never have to edit Home.tsx.
 */

import { useEffect, useState } from 'react';
import { BAKERY_LOCAL } from '../../utils/bakeryLocal';

const API_URL = import.meta.env.VITE_API_URL || '';

export type PreviewProduct = {
  id?: string;
  name: string;
  src: string;
  alt: string;
  price?: number;
};

export type PreviewCampaign = {
  id: string;
  slug: string;
  title: string;
  hero_text: string | null;
  hero_image_url: string | null;
  menu_item_image_url: string | null;
  organizer_name: string | null;
};

export type PreviewContact = {
  phone: string;
  address: string;
  hours: string;
};

const FALLBACK_FEATURED: PreviewProduct[] = [
  {
    src: '/assets/images/products/product-chocolate-cake-ganache-ferrero-rocher.webp',
    alt: 'Chocolate Ferrero Rocher Cake',
    name: 'Chocolate Ferrero Rocher Cake',
  },
  {
    src: '/assets/images/products/product-birthday-cake-first-birthday-teddy-bear.webp',
    alt: 'First Birthday Teddy Bear Cake',
    name: 'First Birthday Teddy Bear Cake',
  },
  {
    src: '/assets/images/products/product-cheesecake-slice-caramel-biscoff.webp',
    alt: 'Caramel Biscoff Cheesecake',
    name: 'Caramel Biscoff Cheesecake',
  },
  {
    src: '/assets/images/products/product-cookies-cream-cake-oreo.webp',
    alt: 'Oreo Cookies & Cream Cake',
    name: 'Oreo Cookies & Cream Cake',
  },
];

const FALLBACK_CAKES = [
  '/assets/images/products/product-gender-reveal-cake-boy-or-girl.webp',
  '/assets/images/products/product-wedding-cake-mr-and-mrs.webp',
  '/assets/images/products/product-heart-cake-white-ruffled-borders.webp',
  '/assets/images/products/product-birthday-cake-jungle-safari-animals.webp',
];

export const DEFAULT_PREVIEW_CONTACT: PreviewContact = {
  phone: BAKERY_LOCAL.telephone,
  address: BAKERY_LOCAL.addressFull,
  hours: BAKERY_LOCAL.hoursShort,
};

function mapMenuItems(items: Array<{ id: string; name: string; image_url: string | null; price?: number }>): PreviewProduct[] {
  return items
    .filter((item) => item.image_url)
    .map((item) => ({
      id: item.id,
      name: item.name,
      src: item.image_url as string,
      alt: item.name,
      price: item.price,
    }));
}

export function usePreviewHomeData() {
  // Start empty so the first paint does not flash stock cakes before live menu data arrives.
  const [featured, setFeatured] = useState<PreviewProduct[]>([]);
  const [cakes, setCakes] = useState<string[]>([]);
  const [campaigns, setCampaigns] = useState<PreviewCampaign[]>([]);
  const [contact, setContact] = useState<PreviewContact>(DEFAULT_PREVIEW_CONTACT);

  useEffect(() => {
    const load = async () => {
      try {
        const featuredRes = await fetch(`${API_URL}/api/menu?featured=true`);
        if (featuredRes.ok) {
          const data = await featuredRes.json();
          const mapped = mapMenuItems(data.items || []).slice(0, 6);
          setFeatured(mapped.length > 0 ? mapped : FALLBACK_FEATURED);
        } else {
          setFeatured(FALLBACK_FEATURED);
        }
      } catch {
        // Menu API is down — show stock photos rather than an empty grid.
        setFeatured(FALLBACK_FEATURED);
      }

      try {
        const cakesRes = await fetch(`${API_URL}/api/menu?category=Custom Cakes`);
        if (cakesRes.ok) {
          const data = await cakesRes.json();
          const urls = (data.items || [])
            .map((item: { image_url: string | null }) => item.image_url)
            .filter(Boolean)
            .slice(0, 4);
          setCakes(urls.length > 0 ? urls : FALLBACK_CAKES);
        } else {
          setCakes(FALLBACK_CAKES);
        }
      } catch {
        setCakes(FALLBACK_CAKES);
      }

      try {
        const campaignRes = await fetch(`${API_URL}/api/campaigns/public`);
        if (campaignRes.ok) {
          const data = await campaignRes.json();
          setCampaigns(Array.isArray(data?.campaigns) ? data.campaigns : []);
        }
      } catch {
        // Fundraisers are optional on the preview.
      }

      try {
        const contactRes = await fetch(`${API_URL}/api/settings/contact`);
        if (contactRes.ok) {
          const data = await contactRes.json();
          setContact({
            phone: data.phone || DEFAULT_PREVIEW_CONTACT.phone,
            address: data.address || DEFAULT_PREVIEW_CONTACT.address,
            hours: DEFAULT_PREVIEW_CONTACT.hours,
          });
        }
      } catch {
        // Keep NAP defaults from bakeryLocal.
      }
    };

    load();
  }, []);

  return { featured, cakes, campaigns, contact };
}
